import React, { useEffect, useState } from 'react';
import * as takata from '../../services/takataService';
import { VerificaTakata } from '../../services/takataService';
import { useToast } from '../../contexts/ToastContext';

/**
 * Dati che completano i documenti di una verifica già registrata:
 *  - riferimenti della pratica (numero STA e corrispettivo), per QUALUNQUE esito;
 *  - dati di chi firma la dichiarazione, solo per i veicoli coinvolti.
 *
 * Vive in un componente solo perché serve in due punti: subito dopo la verifica e nel
 * registro. Nel flusso con l'estensione il primo non si vede nemmeno — a verifica conclusa
 * il certificato si scarica da sé e l'operatore viene portato al registro — quindi è lì che
 * questi campi devono essere raggiungibili, altrimenti restano irraggiungibili del tutto.
 */

const ACQ_VUOTO = {
  nome: '', cognome: '', natoA: '', natoIl: '', codiceFiscale: '', residenza: '',
  perSocieta: false, denominazione: '', sedeLegale: '', partitaIva: '', qualifica: '',
};

const INPUT = 'px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm';

interface Props {
  verifica: VerificaTakata;
  onSaved: (v: VerificaTakata) => void;
}

export default function DatiDocumento({ verifica, onSaved }: Props) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [pratica, setPratica] = useState('');
  const [corrispettivo, setCorrispettivo] = useState('');
  const [acq, setAcq] = useState<any>(ACQ_VUOTO);

  const coinvolto = ['richiamo', 'stop_drive', 'altri_richiami'].includes(verifica.esito);

  // I campi ripartono dai valori già salvati: si apre il pannello di una verifica vecchia
  // e si trova quello che c'era, non un modulo vuoto.
  useEffect(() => {
    setPratica(verifica.numeroPraticaSTA || '');
    setCorrispettivo(verifica.corrispettivo?.importo ? String(verifica.corrispettivo.importo) : '');
    setAcq({ ...ACQ_VUOTO, ...(verifica.datiAcquirente || {}) });
  }, [verifica._id, verifica.numeroPraticaSTA, verifica.datiAcquirente, verifica.corrispettivo]);

  // `null` = valore rifiutato, `undefined` = campo lasciato vuoto (legittimo).
  const leggiCorrispettivo = (): { importo: number; ivaInclusa: boolean } | undefined | null => {
    if (!corrispettivo.trim()) return undefined;
    const imp = parseFloat(corrispettivo.replace(',', '.'));
    if (isNaN(imp) || imp < 0) { toast.error('Corrispettivo non valido'); return null; }
    return { importo: imp, ivaInclusa: true };
  };

  const salva = async (conAcquirente: boolean) => {
    const corr = leggiCorrispettivo();
    if (corr === null) return;
    setLoading(true);
    try {
      const r = await takata.salvaDatiAcquirente(verifica._id, {
        numeroPraticaSTA: pratica,
        corrispettivo: corr,
        ...(conAcquirente ? { datiAcquirente: acq } : {}),
      });
      onSaved(r.verifica);
      toast.success(conAcquirente ? 'Dati salvati: riscarica i documenti' : 'Riferimenti salvati: riscarica il certificato');
    } catch (e) {
      toast.error('Errore nel salvataggio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Riferimenti della pratica</h3>
        <p className="text-2xs text-gray-500 dark:text-gray-400 mb-2">
          Compaiono sul certificato. Il numero di pratica viene anteposto al nome del file scaricato.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Numero pratica STA (es. 000375T26)" value={pratica} onChange={(e) => setPratica(e.target.value)} className={INPUT} />
          <input placeholder="Corrispettivo € (opzionale)" value={corrispettivo} onChange={(e) => setCorrispettivo(e.target.value)} className={INPUT} />
        </div>
        {!coinvolto && (
          <button onClick={() => salva(false)} disabled={loading} className="mt-3 px-4 py-2 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-sm font-semibold disabled:opacity-50">
            Salva riferimenti
          </button>
        )}
      </div>

      {coinvolto && (
        <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Dati per la dichiarazione</h3>

          {/* Il veicolo può essere intestato a un'impresa: in quel caso chi firma non è
              l'acquirente ma chi la rappresenta, e la dichiarazione deve dirlo. */}
          <div className="flex items-center gap-4 mb-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
              <input type="radio" checked={!acq.perSocieta} onChange={() => setAcq({ ...acq, perSocieta: false })} className="accent-gray-900 dark:accent-gray-300" />
              Persona fisica
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
              <input type="radio" checked={acq.perSocieta} onChange={() => setAcq({ ...acq, perSocieta: true })} className="accent-gray-900 dark:accent-gray-300" />
              Società (firma il legale rappresentante)
            </label>
          </div>

          {acq.perSocieta && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input placeholder="Denominazione società" value={acq.denominazione} onChange={(e) => setAcq({ ...acq, denominazione: e.target.value })} className={INPUT} />
              <input placeholder="P.IVA / C.F. società" value={acq.partitaIva} onChange={(e) => setAcq({ ...acq, partitaIva: e.target.value.toUpperCase() })} className={`${INPUT} uppercase`} />
              <input placeholder="Sede legale" value={acq.sedeLegale} onChange={(e) => setAcq({ ...acq, sedeLegale: e.target.value })} className={INPUT} />
              <input placeholder="Qualifica (legale rappresentante)" value={acq.qualifica} onChange={(e) => setAcq({ ...acq, qualifica: e.target.value })} className={INPUT} />
            </div>
          )}

          <div className="text-2xs text-gray-500 dark:text-gray-400 mb-2">
            {acq.perSocieta ? 'Dati di chi firma per la società:' : "Dati dell'acquirente:"}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Nome" value={acq.nome} onChange={(e) => setAcq({ ...acq, nome: e.target.value })} className={INPUT} />
            <input placeholder="Cognome" value={acq.cognome} onChange={(e) => setAcq({ ...acq, cognome: e.target.value })} className={INPUT} />
            <input placeholder="Nato/a a" value={acq.natoA} onChange={(e) => setAcq({ ...acq, natoA: e.target.value })} className={INPUT} />
            <input placeholder="Nato/a il (gg/mm/aaaa)" value={acq.natoIl} onChange={(e) => setAcq({ ...acq, natoIl: e.target.value })} className={INPUT} />
            <input placeholder="Codice fiscale" value={acq.codiceFiscale} onChange={(e) => setAcq({ ...acq, codiceFiscale: e.target.value.toUpperCase() })} className={`${INPUT} uppercase`} />
            {!acq.perSocieta && (
              <input placeholder="Residenza" value={acq.residenza} onChange={(e) => setAcq({ ...acq, residenza: e.target.value })} className={INPUT} />
            )}
          </div>

          <button onClick={() => salva(true)} disabled={loading} className="mt-3 px-4 py-2 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-sm font-semibold disabled:opacity-50">
            Salva dati e aggiorna dichiarazione
          </button>
        </div>
      )}
    </div>
  );
}
